import type { CastMember } from "@supermanus/document-model";

export interface CastAssignPickerProps {
  members: CastMember[];
  value?: string;
  onSelect: (name: string) => void;
  onClear: () => void;
}

/** Compact cast chips for the focused cue cell (Ctrl/⌘+1–9).
 *  Shown only when `SHOW_REPLIK_UI` in `replikFeature.ts` is true. */
export function CastAssignPicker({
  members,
  value,
  onSelect,
  onClear,
}: CastAssignPickerProps) {
  const sorted = [...members]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .slice(0, 9);
  if (sorted.length === 0) return null;

  const current = value?.trim().toLowerCase() ?? "";

  return (
    <div
      className="sm-cast-assign"
      role="listbox"
      aria-label="Tildel medvirkende"
    >
      {sorted.map((member, index) => {
        const active = member.name.trim().toLowerCase() === current;
        return (
          <button
            key={member.id}
            type="button"
            role="option"
            aria-selected={active}
            className={`sm-cast-assign-chip${active ? " is-active" : ""}`}
            style={
              member.color
                ? {
                    color: member.color,
                    borderColor: active ? member.color : undefined,
                  }
                : undefined
            }
            title={`${member.name} — Ctrl/⌘+${index + 1}`}
            // Keep TipTap focus so typing continues after pick
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onSelect(member.name)}
          >
            <kbd>{index + 1}</kbd>
            <span>{member.name}</span>
          </button>
        );
      })}
      <button
        type="button"
        className={`sm-cast-assign-chip sm-cast-assign-clear${!current ? " is-active" : ""}`}
        title="Fjern medvirkende — Ctrl/⌘+0"
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClear}
      >
        <kbd>0</kbd>
        <span>Ingen</span>
      </button>
    </div>
  );
}

/** Sorted cast list capped at 9 for keyboard shortcuts. */
export function castShortcutMembers(members: CastMember[]): CastMember[] {
  return [...members].sort((a, b) => a.sortOrder - b.sortOrder).slice(0, 9);
}
